import React, { type CSSProperties, Fragment, useContext, useState } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { toQaClassName } from 'venn-utils';

import { type SelectTypes, type VennColors } from 'venn-ui-kit';
import {
  CheckboxWrapper,
  ColorUtils,
  Ellipsis,
  EllipsisTooltipSpan,
  GetColor,
  Icon,
  ItemIcon,
  ItemType,
  Tooltip,
} from 'venn-ui-kit';
import { CELL_SPACING, getItemType, isUploaded, ItemLabel } from './shared';
import { ColumnRenderers, renderItemsTooltip, VIEW_COLUMNS } from './Columns';
import type { CustomMultiSelectProps, SearchMenuItem } from '../types';
import { itemEqualityCheck } from '../utils';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

const isIncluded = (array: SearchMenuItem[], item: SearchMenuItem) =>
  array.some((arrayItem: SearchMenuItem) => itemEqualityCheck(arrayItem, item)) || false;

const checkFirst = (options: SearchMenuItem[], item: SearchMenuItem) => {
  const taggedIndex = options.findIndex((option) => itemEqualityCheck(option, item));
  const isFirst = !options[taggedIndex - 1]?.isTagged && options[taggedIndex + 1]?.isTagged;

  return isFirst;
};

const checkLast = (options: SearchMenuItem[], item: SearchMenuItem) => {
  const taggedIndex = options.findIndex((option) => itemEqualityCheck(option, item));
  const isLast = options[taggedIndex - 1]?.isTagged && !options[taggedIndex + 1]?.isTagged;

  return isLast;
};

const Option = <IsMulti extends boolean = false>(props: SelectTypes.OptionProps<SearchMenuItem, IsMulti>) => {
  const { options, innerProps, innerRef, getStyles, isDisabled, isSelected, isMulti } = props;
  const selectProps = props.selectProps as unknown as CustomMultiSelectProps;
  const { columns, optionDisabledTooltipContent, excludedItems, smallScreen, refreshedStyling } = selectProps;
  const { Colors } = useContext(ThemeContext);

  // We use state rather than use ref here, so that the component rerenders when the ref is defined
  const [labelCell, setLabelCell] = useState<HTMLTableCellElement | null>();
  const labelWidth = labelCell ? labelCell.offsetWidth - CELL_SPACING : undefined;
  const item = props.data; // as SearchMenuItem;

  const itemType = getItemType(item);
  const isTag = itemType === ItemType.Tag;
  const isTagOpen = isTag ? isIncluded(selectProps.openedTags, item) : undefined;
  const isFirstTaggedItemInDropdown = item.isTagged ? checkFirst([...(options as SearchMenuItem[])], item) : undefined;
  const isLastTaggedItemInDropdown = item.isTagged ? checkLast([...(options as SearchMenuItem[])], item) : undefined;

  const overrideIsSelected = isMulti
    ? isTag
      ? isIncluded(selectProps.selectedTagDropdowns, item)
      : isIncluded(selectProps.selections, item)
    : isSelected;

  const handleTagOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    overrideIsSelected ? selectProps.onBulkTagUnselect(item) : selectProps.onBulkTagSelect(item);
  };

  const isTaggedItemExcluded = item.isTagged
    ? excludedItems?.some(
        (excludedItem) =>
          excludedItem.id === item.searchResult?.fundId || excludedItem.id === String(item.searchResult?.portfolioId),
      )
    : undefined;

  const isOptionDisabled = isTaggedItemExcluded || isDisabled;

  const style = {
    ...getStyles('option', props),
    display: 'table-row',
    color: isOptionDisabled ? Colors.GreyScale.Grey50 : Colors.GreyScale.Grey80,
  } as CSSProperties;

  const displayColumns = item.category === 'view' ? VIEW_COLUMNS : item.category === 'article' ? [] : columns;

  const disabledTooltipContent = isOptionDisabled
    ? isTaggedItemExcluded && item.isTagged
      ? 'Item is already selected'
      : optionDisabledTooltipContent
        ? optionDisabledTooltipContent(item) !== ''
          ? optionDisabledTooltipContent(item)
          : undefined
        : 'This benchmark has no overlap with your selection'
    : undefined;

  return (
    <>
      <ItemRow
        refreshedStyling={refreshedStyling}
        style={style}
        {...innerProps}
        ref={innerRef}
        className={cn(`qa-menu-item-renderer ${toQaClassName(item.label)} ${labelWidth}`, {
          'qa-menu-item-disabled': isOptionDisabled,
        })}
        key={item.label}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          if (isOptionDisabled) {
            return;
          }

          if (!isMulti && isTag) {
            isTagOpen ? selectProps.onTagsClose(item) : selectProps.onTagsDropdown(item);
            return;
          }

          isTag ? handleTagOnClick(e) : innerProps.onClick?.(e);
        }}
        isMulti={isMulti}
        isTagged={item.isTagged}
        Colors={Colors}
        isOptionDisabled={Boolean(isOptionDisabled)}
        isTag={isTag}
        isFocused={props.isFocused}
      >
        {isTag ? (
          <ArrowCell
            data-testid="qa-tag-dropdown"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              isTagOpen ? selectProps.onTagsClose(item) : selectProps.onTagsDropdown(item);
            }}
          >
            <ArrowIcon type={isTagOpen ? 'caret-down' : 'caret-right'} isMulti={isMulti} />
          </ArrowCell>
        ) : (
          <td>
            {item.isTagged ? (
              <TagBorderWrapper
                isFirstTaggedItemInDropdown={isFirstTaggedItemInDropdown}
                isLastTaggedItemInDropdown={isLastTaggedItemInDropdown}
              >
                <div />
              </TagBorderWrapper>
            ) : (
              <div />
            )}
          </td>
        )}

        {isMulti ? (
          <CellWrapper>
            {isOptionDisabled ? (
              <StyledIcon type="ban" />
            ) : refreshedStyling ? (
              <div className="w-[30px] flex items-center justify-center">
                <Checkbox state={overrideIsSelected ? 'checked' : 'unchecked'} />
              </div>
            ) : (
              <CheckboxWrapper
                key={`${item.searchResult?.portfolioId || item.searchResult?.fundId}-${overrideIsSelected}`}
                checked={overrideIsSelected}
                onClick={(e) => e.preventDefault()}
                onKeyUp={(e) => e.preventDefault()}
                onChange={(e) => e.preventDefault()}
                disabled={isOptionDisabled}
              />
            )}
          </CellWrapper>
        ) : null}
        <td className={smallScreen ? 'no-padding' : undefined}>
          {smallScreen ? null : (
            <div className="pt-[3px]">
              <ItemLabel isDisabled={Boolean(isOptionDisabled)} item={item} />
            </div>
          )}
        </td>
        <td>
          <ItemIcon
            isDisabled={isOptionDisabled}
            item={itemType}
            dataSource={item.value?.superItem?.dataSource || item.value?.portfolio?.dataSource}
            isUploaded={isUploaded(item)}
            investmentSource={item.value?.fund?.investmentSource}
          />
        </td>
        <ExpandCell
          uploaded={isUploaded(item)}
          isOptionDisabled={Boolean(isOptionDisabled)}
          isSelected={overrideIsSelected}
          ref={(ref) => setLabelCell(ref)}
        >
          <NameOverflow>
            {smallScreen ? (
              <StyledTooltip usePortal content={disabledTooltipContent ?? renderItemsTooltip(item)}>
                <Ellipsis singleLine>{item.label}</Ellipsis>
              </StyledTooltip>
            ) : (
              <EllipsisTooltipSpan
                usePortal
                maxWidth={labelWidth}
                content={disabledTooltipContent ?? item.label}
                alwaysShowTooltip={isOptionDisabled}
              >
                {item.label}
              </EllipsisTooltipSpan>
            )}
          </NameOverflow>
        </ExpandCell>
        {displayColumns.map((col) => (
          <Fragment key={col}>{ColumnRenderers[col].cellRenderer(item)}</Fragment>
        ))}
      </ItemRow>
      {isTagOpen ? (
        <tr className="bg-venn-grey-10 text-grey-80">
          <td colSpan={5 + columns.length} className="text-[10px] py-[6px] pr-0 pl-[18px]">
            Contents of a tag are search and filter agnostic.
          </td>
        </tr>
      ) : null}
    </>
  );
};

export default Option;

const ItemRow = styled.tr<{
  isOptionDisabled: boolean;
  isMulti: boolean;
  isTagged?: boolean;
  isTag: boolean;
  isFocused: boolean;
  refreshedStyling: boolean;
  Colors: VennColors;
}>`
  min-height: 32px !important;
  position: relative;
  ${(props) =>
    props.isOptionDisabled &&
    css`
      cursor: ${props.isTag ? 'pointer' : 'not-allowed'} !important;
    `}

  ${(props) =>
    props.isMulti &&
    css`
      padding: 0 25px !important;
    `}

  ${(props) =>
    props.isTagged &&
    css`
      background-color: ${props.isOptionDisabled
        ? GetColor.GreyScale.Grey20
        : props.refreshedStyling
          ? ColorUtils.opacifyFrom(GetColor.DarkBlue, 0.1)
          : ColorUtils.hex2rgba(props.Colors.Primary.Dark, 0.05)} !important;

      &:hover {
        background-color: ${GetColor.GreyScale.Grey20} !important;
      }
    `}

  ${(props) =>
    props.isTagged &&
    props.isFocused &&
    css`
      background-color: ${GetColor.GreyScale.Grey20} !important;
    `}
`;

const TagBorderWrapper = styled.div<{
  isFirstTaggedItemInDropdown?: boolean;
  isLastTaggedItemInDropdown?: boolean;
}>`
  height: 30px;
  width: 9px;

  & div {
    border-right: 1px dashed ${GetColor.GreyScale.Grey60};
    height: 100%;
  }

  ${(props) =>
    props.isFirstTaggedItemInDropdown &&
    css`
      padding-top: 8px;
    `}
  ${(props) =>
    props.isLastTaggedItemInDropdown &&
    css`
      padding-bottom: 8px;
    `}
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
`;

const NameOverflow = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  white-space: nowrap;
`;

const ExpandCell = styled.td<{ uploaded: boolean; isOptionDisabled: boolean; isSelected: boolean }>`
  ${(props) =>
    !props.isOptionDisabled &&
    css`
      color: ${GetColor.Black};
    `}
  ${(props) =>
    props.uploaded &&
    css`
      font-weight: bold;
      color: ${props.isOptionDisabled ? GetColor.GreyScale.Grey80 : GetColor.Primary.Dark};
    `}
  width: 100%;
  position: relative;

  :before {
    content: '&nbsp;';
    visibility: hidden;
  }
`;

const ArrowCell = styled.td`
  position: relative;
  padding-left: 5px;
`;

const ArrowIcon = styled(Icon)<{ isMulti: boolean }>`
  top: ${({ isMulti }) => (isMulti ? 9 : 7)}px;
  position: absolute;
  font-size: 14px;
`;

const CellWrapper = styled.td`
  text-align: center;
`;

const StyledIcon = styled(Icon)`
  font-size: 16px;
  color: ${GetColor.GreyScale.Grey40};
`;
